/**
 * Babcock Ranch neighborhoods — the town's villages, in development order.
 *
 * Prices and "for sale" counts are a snapshot read on Oct 25, 2026 from
 * public MLS-fed listing pages (mainly Homes by Marco's subdivision index),
 * the official Babcock Ranch neighborhoods page, and builder pages. Counts
 * move daily; treat them as "as of NEIGHBORHOODS_AS_OF", not live data.
 * Where a field could not be verified from a source actually read, the
 * entry says so instead of guessing.
 */

export type DistrictId = 'westtown' | 'lake-babcock' | 'national' | 'midtown';

export interface District {
  id: DistrictId;
  title: string;
  /** One line under the district title. */
  blurb: string;
  /** Palette color for the banner, used as the fallback if the photo fails to load. */
  color: string;
  /** Photo of a representative neighborhood in this district, from its public sales page. */
  photoUrl: string;
  photoAlt: string;
}

export interface Neighborhood {
  id: string;
  district: DistrictId;
  name: string;
  /** One line shown under the name while the entry is collapsed. */
  summary: string;
  /** Real photo from the neighborhood's public sales page. */
  photoUrl: string;
  /** The public sales page the photo comes from (linked from the entry). */
  photoPage: string;
  /** Short price label for the stat tiles and price bar, e.g. "$398K–$625K". */
  priceText: string;
  /** Listing-range ends in $K, for the price bar. Absent when unpublished or open-ended. */
  priceMinK?: number;
  priceMaxK?: number;
  /** Approximate homes for sale as of NEIGHBORHOODS_AS_OF; absent when not tracked (builder/pre-sales/rental). */
  forSaleCount?: number;
  status: string;
  gated?: boolean;
  golf?: boolean;
  fiftyFivePlus?: boolean;
  builder: string;
  /** Short age label for the stat tile, e.g. "Opened 2017". */
  ageShort: string;
  /** Narrative take — desirability, prestige, target buyer, best features. */
  narrative: string;
  /** One-to-two-sentence teaser shown on the collapsed card. */
  takeShort: string;
  priceRange: string;
  /** When it was built / opened, and whether it is still building. */
  built: string;
  /** Neighborhood-specific amenities only; everything else is town-wide. */
  amenities: string;
  /** Homes on the market as of NEIGHBORHOODS_AS_OF. */
  forSale: string;
  sourceNote: string;
}

export const NEIGHBORHOODS_AS_OF = 'October 2026';

/** Town-wide, for every resident — not credited to any one neighborhood below. */
export const COMMUNITY_AMENITIES_NOTE =
  'Every neighborhood also shares the town-wide amenities: Founder\u2019s Square, the Lake Timber Lodge and Cypress Lodge pools, the Lee Health Healthy Life Center, 50+ miles of trails, the community garden, Babcock Neighborhood School, and fishing and kayaking on Lake Babcock.';

/**
 * Grouping follows the developer's own districts where Kitson & Partners
 * publishes them (WestTown and MidTown, per the official neighborhoods
 * page and the master plan), with the established lakeside neighborhoods
 * grouped by the shore they sit on and Babcock National standing alone as
 * its own enclave. The Preserve and The Flatwoods are not district-tagged
 * by the developer (sold out / rental), so they are grouped by location.
 */
export const DISTRICTS: District[] = [
  {
    id: 'westtown',
    title: 'WestTown — the original town',
    blurb: 'Anchored by Founder’s Square, where Babcock Ranch started — wrapped around Lake Timber on the west side.',
    color: '#173D4E',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2021/09/Lake-Timber-min.jpg',
    photoAlt: 'Lake Timber, Babcock Ranch',
  },
  {
    id: 'lake-babcock',
    title: 'Lake Babcock — the east shore',
    blurb: 'Custom and lakefront homes ringing the 300-acre lake, east of Founder’s Square.',
    color: '#2C5B70',
    photoUrl:
      'https://sierra-public.azureedge.net/33fb43bc-d273-40f8-bf45-af8d35c8e447.jpg',
    photoAlt: 'The Lake Babcock shore',
  },
  {
    id: 'national',
    title: 'Babcock National — the golf enclave',
    blurb: 'The separately gated golf community on the lake’s northeast side, with its own club, HOA and membership.',
    color: '#416A4A',
    photoUrl:
      'https://sierra-public.azureedge.net/10bc38d2-6dd8-49c6-b79d-7dcabb7e776b.jpg',
    photoAlt: 'Babcock National',
  },
  {
    id: 'midtown',
    title: 'MidTown — the new side of town',
    blurb: 'The developer’s MidTown district — the newest neighborhoods, most of the current construction, and the future downtown core.',
    color: '#A8823C',
    photoUrl:
      'https://babcockranch.com/wp-content/uploads/2023/07/TuckersCove_ShipwreckTavern_Website.jpg',
    photoAlt: 'Tucker’s Cove, Babcock Ranch',
  },
];

export const NEIGHBORHOODS: Neighborhood[] = [
  {
    id: 'lake-timber',
    district: 'westtown',
    name: 'Lake Timber',
    summary: 'From the $400s to $1M+ \u00b7 ~14 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2021/09/Lake-Timber-min.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/lake-timber/',
    priceText: 'From the $400s to $1M+',
    priceMinK: 400,
    priceMaxK: 1050,
    forSaleCount: 14,
    status: 'Established · still building',
    builder: 'Pulte + custom builders',
    ageShort: 'Opened 2017',
    narrative: 'The prestige address of the original town — the first neighborhood built, and still the one with the most character. Custom and semi-custom homes, mature landscaping, and the Lake House clubhouse on the water give it a settled, nothing-left-to-prove feel. Best for move-up buyers who want the established side of the Ranch and an easy walk or cart ride to Founder\u2019s Square. Best features: the lakefront Lake House with its pool and kayak dock, and homes that don\u2019t all look alike.',
    takeShort: 'The prestige address of the original town — the first neighborhood built, and still the one with the most character.',
    priceRange: 'New homes from the $400s to over $1 million; active resales average about $730K.',
    built: 'The original Babcock Ranch neighborhood \u2014 opened 2017, homes built 2017\u20132025. Established, with infill and custom homes still building.',
    amenities: 'The Lake House, a resident-only lakefront clubhouse with pool, wraparound decks, playground, grill area and a kayak/canoe dock; plus a fishing dock, dog park, town garden and trailhead.',
    forSale: 'About 14 homes listed, averaging roughly $720K\u2013$737K.',
    sourceNote: 'Official neighborhoods page; Homes by Marco (Lake Timber); Kimley-Horn project history.',
  },
  {
    id: 'parkside',
    district: 'westtown',
    name: 'Parkside',
    summary: '$398K\u2013$625K \u00b7 12 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/0b095c3e-0765-41bf-ae0b-ca53f4efd57f.jpg',
    photoPage: 'https://www.greaterftmyers.com/parkside/',
    priceText: '$398K–$625K',
    priceMinK: 398,
    priceMaxK: 625,
    forSaleCount: 12,
    status: 'Built out',
    builder: 'Pulte Homes',
    ageShort: 'Built late 2010s',
    narrative: 'The quiet value play in the original town. Parkside sits beside a big park, minutes from Founder\u2019s Square, with a median around $450K — the established streets without Lake Timber\u2019s custom-home prices. Best for families and full-timers who want the walkable core of the Ranch on a sensible budget. Best features: the park-side setting and a built-out neighborhood where what you see is what you get.',
    takeShort: 'The quiet value play in the original town — established streets near Founder\u2019s Square, median around $450K.',
    priceRange: 'Active listings $398,305\u2013$625,000; median about $450,000.',
    built: 'The second neighborhood developed, by Pulte Homes, in the late 2010s. Established and effectively built out; an exact opening year is not published.',
    amenities: 'No private amenity center of its own \u2014 it sits next to a spacious park and close to Founder\u2019s Square, and uses the town-wide amenities.',
    forSale: '12 single-family homes listed.',
    sourceNote: 'Greater Fort Myers Realty neighborhood guide; Homes by Marco subdivision index.',
  },
  {
    id: 'trails-edge',
    district: 'westtown',
    name: 'Trail\u2019s Edge',
    summary: '$240K\u2013$765K \u00b7 ~52 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/f6a9be37-76ca-4c11-9871-296d186be03b.jpg',
    photoPage: 'https://www.greaterftmyers.com/trails-edge/',
    priceText: '$240K–$765K',
    priceMinK: 240,
    priceMaxK: 765,
    forSaleCount: 52,
    status: 'Built out',
    builder: 'Lennar',
    ageShort: 'First homes 2018',
    narrative: 'The resale workhorse of Babcock Ranch. With roughly 52 homes on the market — the most in town — and prices from $240K to $765K, it\u2019s where choice and negotiating room live. It\u2019s fully built out, so there\u2019s no construction noise and no surprises. Best for first-time Ranch buyers, value hunters, and investors. Best features: the widest selection in town and a walk-to-school, walk-to-the-Square location.',
    takeShort: 'The resale workhorse — roughly 52 homes on the market, the most in town, from $240K to $765K.',
    priceRange: 'Active listings $240,000\u2013$765,000, plus one condo at $255,000 \u2014 the widest spread in the established core.',
    built: 'The third neighborhood; first residents moved in June 30, 2018. Built by Lennar in three collections (twin villas, executive and manor homes). Fully built out.',
    amenities: 'No private clubhouse; lakefront lots and an easy walk to the schools and Founder\u2019s Square.',
    forSale: 'About 52 listings (51 single-family + 1 condo) \u2014 the largest resale inventory in town.',
    sourceNote: 'Kitson & Partners milestones; Homes by Marco subdivision index.',
  },
  {
    id: 'lake-babcock',
    district: 'lake-babcock',
    name: 'Lake Babcock',
    summary: '$329K\u2013$1.6M \u00b7 ~13\u201317 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/33fb43bc-d273-40f8-bf45-af8d35c8e447.jpg',
    photoPage: 'https://www.greaterftmyers.com/lake-babcock/',
    priceText: '$329K–$1.6M',
    priceMinK: 329,
    priceMaxK: 1599,
    forSaleCount: 15,
    status: 'Resale only',
    builder: 'Seven builders · custom + luxury',
    ageShort: 'Built 2018–2024',
    narrative: 'The lakefront prestige play. Custom and luxury homes ring the 300-acre lake, some with private docks, and the neighborhood is sold out — scarcity is part of the appeal, and prices run to $1.6M. Best for buyers who measure a home by its sunset and want space for a boat. Best features: the lake itself, dockable frontage, and custom architecture you can\u2019t get in the production neighborhoods.',
    takeShort: 'The lakefront prestige play — custom homes around the 300-acre lake, some with private docks, to $1.6M.',
    priceRange: 'Active listings $329,000\u2013$1,599,000; custom and luxury builders around the 300-acre lake push the top end.',
    built: 'Homes built 2018\u20132024 by seven builders. Sold out \u2014 resale only.',
    amenities: 'Rings Lake Babcock; lakefront homes may have private docks. No private clubhouse \u2014 Cypress Lodge and the Lake Babcock boardwalk are adjacent.',
    forSale: 'About 13\u201317 single-family homes, averaging about $767K.',
    sourceNote: 'Homes by Marco (Lake Babcock).',
  },
  {
    id: 'lake-babcock-estates',
    district: 'lake-babcock',
    name: 'Lake Babcock Estates',
    summary: 'Custom estate enclave \u00b7 no separate listing data',
    photoUrl: 'https://sierra-public.azureedge.net/33fb43bc-d273-40f8-bf45-af8d35c8e447.jpg',
    photoPage: 'https://www.greaterftmyers.com/lake-babcock/',
    priceText: 'Not published',
    status: 'Established enclave',
    builder: 'Custom builders',
    ageShort: 'Lake Babcock enclave',
    narrative: 'The quiet top of the market. The Estates are the custom enclave within the lakefront — the address locals mention when they talk about the Ranch\u2019s best homes. Public data is thin and listings rarely surface, which is exactly the point: this is a buy-it-when-it-appears market. Best for privacy-first luxury buyers playing a long game. Best features: exclusivity and Lake Babcock frontage.',
    takeShort: 'The quiet top of the market — the custom estate enclave on Lake Babcock, where listings rarely surface.',
    priceRange: 'Not published separately. It reads as the custom-estate enclave on Lake Babcock (top-of-market pricing), but no price data was verified \u2014 check before quoting a range.',
    built: 'Named as a distinct neighborhood in the HOA bulletin roster alongside Lake Babcock; build dates are not published.',
    amenities: 'Lake Babcock frontage; nothing further verified.',
    forSale: 'No separate count \u2014 listing sites fold these homes into the Lake Babcock totals.',
    sourceNote: 'Babcock Ranch HOA Babcock Bulletin roster, Jan 2026.',
  },
  {
    id: 'edgewater',
    district: 'westtown',
    name: 'Edgewater',
    summary: '$421K\u2013$750K \u00b7 ~5\u201310 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/5b48a51c-aaf1-46ff-a185-2632bbcee210.jpg',
    photoPage: 'https://www.greaterftmyers.com/edgewater-babcock-ranch/',
    priceText: '$421K–$750K',
    priceMinK: 421,
    priceMaxK: 750,
    forSaleCount: 8,
    status: 'Built out',
    gated: true,
    builder: 'Pulte Homes',
    ageShort: 'Built 2020–2024',
    narrative: 'Gated polish on Lake Timber. Pulte built Edgewater in the early 2020s, so the homes are newer than the original core, and it has its own amenity center — pool, clubhouse, fire pit — behind the gate. Best for buyers who want gated, amenity-rich living on the west side without MidTown prices. Best features: the private amenity center and lake-and-preserve views.',
    takeShort: 'Gated polish on Lake Timber — newer Pulte homes with their own pool, clubhouse, and fire pit.',
    priceRange: 'Active listings $420,900\u2013$749,900.',
    built: 'Built 2020\u20132024 by Pulte Homes \u2014 a gated enclave of about 166 single-family homes on Lake Timber. Essentially built out.',
    amenities: 'Private amenity center with pool, clubhouse and fire pit; premium lake and preserve views.',
    forSale: 'About 5\u201310 single-family homes.',
    sourceNote: 'Homes by Marco (Edgewater); FloridaLuxuryRE MLS snapshot, Oct 14, 2026.',
  },
  {
    id: 'edgewater-shores',
    district: 'lake-babcock',
    name: 'Edgewater Shores',
    summary: '$499K\u2013$705K \u00b7 ~7 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/00bc9757-27cf-4520-9cb7-861d0192aa41.jpg',
    photoPage: 'https://www.greaterftmyers.com/edgewater-shores/',
    priceText: '$499K–$705K',
    priceMinK: 499,
    priceMaxK: 705,
    forSaleCount: 7,
    status: 'Built out',
    gated: true,
    builder: 'Pulte Homes',
    ageShort: 'Built 2022–2024',
    narrative: 'Gated lake living, newer than it looks. Pulte built these 101 homes in 2022–2024, and the signature perk is a residents-only boat launch on Lake Babcock — no trailering to a public ramp. Best for boaters and kayakers who want a newer home behind a gate. Best features: the private launch and a small, finished neighborhood feel.',
    takeShort: 'Gated lake living with a residents-only boat launch on Lake Babcock — no trailering to a public ramp.',
    priceRange: 'Resales seen at $499,000\u2013$705,000; active listings average about $687,000.',
    built: 'Built 2022\u20132024 by Pulte Homes \u2014 a gated enclave of 101 single-family homes. Built out.',
    amenities: 'Private pool and a residents-only boat launch on Lake Babcock; lakefront homesites.',
    forSale: 'About 7 single-family homes.',
    sourceNote: 'Homes by Marco school-area data; Greater Fort Myers Realty.',
  },
  {
    id: 'crescent-grove',
    district: 'lake-babcock',
    name: 'Crescent Grove',
    summary: '$265K\u2013$469K \u00b7 ~5\u20136 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/312fbe9e-e980-4f64-9225-c8bd260836a2.jpg',
    photoPage: 'https://www.greaterftmyers.com/crescent-grove/',
    priceText: '$265K–$469K',
    priceMinK: 265,
    priceMaxK: 469,
    forSaleCount: 6,
    status: 'Resale only',
    builder: 'Meritage Homes',
    ageShort: 'Built 2022–2023',
    narrative: 'Small-scale value. Just 68 Meritage homes, sold out, with resales from the mid-$260s and landscaping included in a low HOA — one of the calmest, cheapest ways to own in the Ranch. Best for budget-conscious buyers and downsizers who don\u2019t need a private amenity center. Best features: the price, and simple, low-fuss ownership.',
    takeShort: 'Small-scale value — just 68 sold-out Meritage homes, resales from the mid-$260s, landscaping included.',
    priceRange: 'Active listings $265,000\u2013$469,000 \u2014 one of the town\u2019s most affordable neighborhoods.',
    built: 'Built 2022\u20132023 by Meritage Homes; only 68 single-family homes. Sold out \u2014 resale only.',
    amenities: 'No private amenity center; landscaping is included in a low HOA fee. Uses the town-wide amenities.',
    forSale: 'About 5\u20136 single-family homes.',
    sourceNote: 'Homes by Marco (Crescent Grove).',
  },
  {
    id: 'the-preserve',
    district: 'midtown',
    name: 'The Preserve',
    summary: '$260K\u2013$320K typical \u00b7 small resale pool',
    photoUrl: 'https://sierra-public.azureedge.net/a6a7d21c-498c-4fd0-b833-a95735d28d1f.jpg',
    photoPage: 'https://www.greaterftmyers.com/preserve-at-babcock-ranch/',
    priceText: '$260K–$320K',
    priceMinK: 260,
    priceMaxK: 320,
    forSaleCount: 1,
    status: 'Resale only',
    builder: 'D.R. Horton',
    ageShort: 'Built 2023–2024',
    narrative: 'The villa value play. The Preserve is one product done simply — D.R. Horton\u2019s Carrington twin villa, 154 of them around four lakes, resales mostly $260K–$320K, with a pool, cabana, and bocce court. Best for downsizers and snowbirds who want low maintenance and a predictable floor plan. The catch: it\u2019s resale-only and listings are scarce, so you buy when one surfaces. Best features: price per amenity, and water views almost everywhere.',
    takeShort: 'The villa value play — one twin-villa floorplan around four lakes, resales mostly $260K–$320K.',
    priceRange: 'Twin villas: recent sales ran $260,000\u2013$320,000 (median $284,500). One product only \u2014 D.R. Horton\u2019s Carrington twin villa, 1,503 sq ft.',
    built: '154 twin villas, all built 2023\u20132024; the builder closed out in May 2024. Resale only.',
    amenities: 'Private pool, cabana/clubhouse and bocce ball court; anchored by four lakes with water views throughout.',
    forSale: 'A small resale pool \u2014 the last public count showed just 1 listed, with steady turnover (14 sales in the prior 12 months).',
    sourceNote: 'McGreevy & Comisar Preserve market report (MLS records to Aug 2026); Greater Fort Myers Realty.',
  },
  {
    id: 'babcock-national',
    district: 'national',
    name: 'Babcock National',
    summary: 'Condos from $199K \u00b7 ~75\u201380 for sale',
    photoUrl: 'https://sierra-public.azureedge.net/10bc38d2-6dd8-49c6-b79d-7dcabb7e776b.jpg',
    photoPage: 'https://www.greaterftmyers.com/babcock-national/',
    priceText: 'Condos $199K+ · homes to $1.1M',
    priceMinK: 199,
    priceMaxK: 1099,
    forSaleCount: 78,
    status: 'Built out',
    gated: true,
    golf: true,
    builder: 'Lennar',
    ageShort: 'Built 2019–2025',
    narrative: 'The golf lifestyle, bundled. Every home comes with a deeded golf membership, and with condos from $199K it\u2019s also the least expensive way into Babcock Ranch with a country club attached — clubhouse dining, resort pool, fitness and sauna, guard gate. Roughly 78 listings, the most in town, means real negotiating power. Best for golfers, snowbird condo buyers, and anyone who wants the club life without a separate initiation check. Best features: bundled golf and depth of choice.',
    takeShort: 'The golf lifestyle, bundled — deeded golf membership with every home, condos from $199K, ~78 listings.',
    priceRange: 'Condos $199,000\u2013$585,000; single-family $499,000\u2013$1,099,000.',
    built: 'Built 2019\u20132025 by Lennar; the golf course opened in February 2020. Essentially built out. A separately gated golf enclave within Babcock Ranch, with its own club and HOA.',
    amenities: 'Private to Babcock National: 18-hole championship golf (bundled membership deeded with the home), clubhouse dining, resort-style pool, fitness center with sauna, tennis and pickleball, guard-gated entry.',
    forSale: 'About 75\u201380 listings (roughly 60 condos and 15\u201318 single-family homes) \u2014 the town\u2019s largest inventory.',
    sourceNote: 'Homes by Marco (Babcock National); Babcock Ranch Telegraph; Lennar.',
  },
  {
    id: 'northridge',
    district: 'westtown',
    name: 'Northridge',
    summary: '$343K\u2013$679K \u00b7 33 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2022/01/Northridge_website-image.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/northridge/',
    priceText: '$343K–$679K',
    priceMinK: 343,
    priceMaxK: 679,
    forSaleCount: 33,
    status: 'Still building',
    builder: 'Pulte Homes',
    ageShort: 'Opened early 2020s',
    narrative: 'New-construction value in WestTown. Pulte is still building here, and with 33 homes for sale it offers the most new-home choice on the west side — warranties, modern floor plans, prices from the $340s. Best for families who want a new house near Founder\u2019s Square rather than a resale project. Best features: selection, and larger plans running to the high $600s for multi-generational layouts.',
    takeShort: 'New-construction value in WestTown — Pulte still building, 33 for sale, from the $340s.',
    priceRange: 'Active listings $342,590\u2013$679,000.',
    built: 'Pulte Homes \u2014 322 single-family homes in WestTown. Opened in the early 2020s and still building.',
    amenities: 'None of its own; residents use the town-wide amenities.',
    forSale: '33 single-family homes \u2014 among the largest new-construction inventories in town.',
    sourceNote: 'Homes by Marco subdivision index; official neighborhoods page.',
  },
  {
    id: 'crescent-lakes',
    district: 'midtown',
    name: 'Crescent Lakes',
    summary: 'New from the mid-$200s \u00b7 ~40\u201349 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/05/Crescent-Lakes_Jay_website-1063x709-1.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/crescent-lakes/',
    priceText: 'New from the mid-$200s',
    priceMinK: 250,
    forSaleCount: 45,
    status: 'Still building',
    gated: true,
    builder: 'Meritage Homes',
    ageShort: 'Building since 2023',
    narrative: 'The new-home volume pick. Meritage\u2019s gated community has the largest new-construction inventory in town — around 45 homes — starting from the mid-$200s, with its own pool, cabana, and pickleball. Best for families who want new, gated, and affordable, with floor plans to compare side by side. Best features: choice and price, in a finished amenity package.',
    takeShort: 'The new-home volume pick — the largest new-construction inventory in town, gated, from the mid-$200s.',
    priceRange: 'New homes from the mid-$200s; active listings average about $380K.',
    built: 'Meritage Homes, gated, in MidTown. Building since 2023 and still active.',
    amenities: 'Private amenity center with pool, cabana, pickleball courts and a sports court; many lake and preserve views.',
    forSale: 'About 40\u201349 single-family homes \u2014 the largest single-neighborhood new-home inventory.',
    sourceNote: 'Official neighborhoods page; Homes by Marco.',
  },
  {
    id: 'creekside-run',
    district: 'midtown',
    name: 'Creekside Run',
    summary: '$346K\u2013$698K \u00b7 19 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2022/10/CreeksideRun_Magnolia_Website_1063x709.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/creekside-run-coming-soon/',
    priceText: '$346K–$698K',
    priceMinK: 346,
    priceMaxK: 698,
    forSaleCount: 19,
    status: 'Still building',
    gated: true,
    builder: 'Dream Finders Homes',
    ageShort: 'Building since 2023',
    narrative: 'The gated family all-rounder. Dream Finders packs a lot in: amenity center, pool, playground, courts for tennis, pickleball, basketball and volleyball, a soccer field, dog park, and lake access — strong amenity value per dollar. Best for active families who\u2019ll actually use it all. Best features: the breadth of the sports and play facilities.',
    takeShort: 'The gated family all-rounder — pool, playground, five kinds of courts, soccer field, dog park, lake access.',
    priceRange: 'Active listings $345,990\u2013$698,099.',
    built: 'Gated MidTown enclave of 291 single-family homes; built 2023 to today and still building (Dream Finders Homes).',
    amenities: 'Amenity center, pool and playground, plus tennis, pickleball, basketball and volleyball courts, a soccer field, dog park, picnic areas and lake access.',
    forSale: '19 single-family homes, 9 of them new construction.',
    sourceNote: 'Homes by Marco (Creekside Run); official neighborhoods page.',
  },
  {
    id: 'palmetto-landing',
    district: 'midtown',
    name: 'Palmetto Landing',
    summary: '$260K\u2013$474K \u00b7 36 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/08/Palmetto-Landing-Amenity-website.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/palmetto-landing/',
    priceText: '$260K–$474K',
    priceMinK: 260,
    priceMaxK: 474,
    forSaleCount: 36,
    status: 'Still building',
    gated: true,
    builder: 'D.R. Horton',
    ageShort: 'Building since 2024',
    narrative: 'D.R. Horton value, two ways. This 499-home gated community mixes single-family homes and twin villas from about $260K, with a pool, splash pad, bocce, clubhouse, and fitness center. Best for first buyers, young families, and villa downsizers in MidTown. Best features: the range of product at the price — and a splash pad that earns its keep with grandkids.',
    takeShort: 'D.R. Horton value two ways — single-family homes and twin villas from about $260K, splash pad included.',
    priceRange: 'Active listings $274,999\u2013$473,675; new homes from $259,990.',
    built: 'D.R. Horton \u2014 gated MidTown community of 499 single-family homes and twin villas; building since 2024.',
    amenities: 'Private pool, splash pad and bocce ball court, plus a clubhouse and fitness center.',
    forSale: '36 single-family homes, 10 of them new construction.',
    sourceNote: 'Homes by Marco (Palmetto Landing); official neighborhoods page.',
  },
  {
    id: 'verde',
    district: 'midtown',
    name: 'Verde',
    summary: '$310K\u2013$635K \u00b7 19 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/04/Verde-website.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/verde/',
    priceText: '$310K–$635K',
    priceMinK: 310,
    priceMaxK: 635,
    forSaleCount: 19,
    status: 'Still building',
    gated: true,
    builder: 'Pulte Homes',
    ageShort: 'Opened mid-2020s',
    narrative: 'Pulte polish on the greenway. Verde\u2019s 399 gated homes border a 3.5-mile green-space corridor linking six new parks, so the neighborhood feels greener and more open than most of MidTown. Best for buyers who want a new Pulte home with nature out the back door. Best features: the parks corridor frontage and preserve-edge lots.',
    takeShort: 'Pulte polish on the greenway — gated homes bordering a 3.5-mile green corridor linking six parks.',
    priceRange: 'Active listings $319,900\u2013$599,990; builder plans $310,990\u2013$634,500.',
    built: 'Pulte Homes \u2014 gated, 399 single-family homes on 109 acres in MidTown. Opened in the mid-2020s and still building.',
    amenities: 'Marketed with private amenities, bordering a 3.5-mile green-space corridor that links six new parks; specific amenity-center features are not published yet.',
    forSale: '19 single-family homes.',
    sourceNote: 'Official neighborhoods page; Homes by Marco subdivision index.',
  },
  {
    id: 'the-sanctuary',
    district: 'midtown',
    name: 'The Sanctuary',
    summary: '$320K\u2013$732K \u00b7 13 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2022/10/6R2A9819-copy-1024x563.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/the-sanctuary/',
    priceText: '$320K–$732K',
    priceMinK: 320,
    priceMaxK: 732,
    forSaleCount: 13,
    status: 'Still building',
    gated: true,
    builder: 'William Ryan Homes',
    ageShort: 'Opened mid-2020s',
    narrative: 'Preserve-side calm. William Ryan\u2019s gated community sits next to Cypress Creek Preserve, trading the bustle of the village centers for trails and trees, while keeping a central pool, splash pad, and event lawn. Best for nature lovers and families who want the quieter edge of MidTown. Best features: the preserve next door and a laid-back, green setting.',
    takeShort: 'Preserve-side calm — gated, next to Cypress Creek Preserve, with pool, splash pad, and event lawn.',
    priceRange: 'Active listings $325,000\u2013$731,900.',
    built: 'William Ryan Homes \u2014 gated, 476 single-family homes in MidTown. Mid-2020s, still building.',
    amenities: 'Central amenity with pool, splash pad, event lawn and play area; next to the Cypress Creek Preserve park.',
    forSale: '13 homes.',
    sourceNote: 'Homes by Marco (The Sanctuary); official neighborhoods page.',
  },
  {
    id: 'terrawalk',
    district: 'westtown',
    name: 'TerraWalk',
    summary: '$300K\u2013$869K \u00b7 builder homes available',
    photoUrl: 'https://babcockranch.com/nitropack_static/ZmAunjjXOPhVQlUxjxaOPuKXgJYJXDKv/assets/images/optimized/rev-364421c/babcockranch.com/wp-content/uploads/2023/04/TerraWalk.jpeg',
    photoPage: 'https://babcockranch.com/neighborhoods/terrawalk/',
    priceText: '$300K–$869K',
    priceMinK: 300,
    priceMaxK: 869,
    status: 'Still building',
    gated: true,
    builder: 'DiVosta',
    ageShort: 'Opened mid-2020s',
    narrative: 'The resort enclave. DiVosta\u2019s 1,000-home community has the deepest private amenity package on the west side — grand clubhouse, resort pool, its own restaurant, courts, fitness, dog park — a community within the community. Best for social, active buyers who want everything inside their own gate. Best features: the amenity depth. One quirk to know: it carries Alva addresses on the Lee County side of the Ranch.',
    takeShort: 'The resort enclave — DiVosta\u2019s 1,000 homes with a grand clubhouse, resort pool, and its own restaurant.',
    priceRange: 'Builder pricing $299,990\u2013$868,990 across single-family homes and attached villas.',
    built: 'DiVosta \u2014 1,000 homes planned; mid-2020s and actively building. Note: TerraWalk carries Alva, FL addresses, on the Lee County side of the Ranch.',
    amenities: 'Extensive and private: grand clubhouse, resort pool, restaurant, fire-pit lounge, pickleball, tennis and bocce courts, fitness center, event lawn, dog park and internal trails.',
    forSale: 'Builder quick-move-in homes are available now; an exact MLS count is not published.',
    sourceNote: 'DiVosta (TerraWalk); official neighborhoods page.',
  },
  {
    id: 'tuckers-cove',
    district: 'midtown',
    name: 'Tucker\u2019s Cove',
    summary: '$250K\u2013$761K \u00b7 ~39 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/07/TuckersCove_ShipwreckTavern_Website.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/tuckers-cove/',
    priceText: '$250K–$761K',
    priceMinK: 250,
    priceMaxK: 761,
    forSaleCount: 39,
    status: 'Still building',
    gated: true,
    builder: 'Lennar',
    ageShort: 'First homes 2024',
    narrative: 'The fun one. Tucker\u2019s Cove is built around a waterpark — lazy river, water slide, luau beach, pirate-ship splash feature — plus the Shipwreck Tavern for the grown-ups. It\u2019s the neighborhood grandkids vote for. Best for families and grandparents who host a lot. Best features: the waterpark, full stop — nothing else in the Ranch is like it.',
    takeShort: 'The fun one — built around a waterpark with lazy river, slide, and luau beach. The neighborhood grandkids vote for.',
    priceRange: 'Active listings $249,999\u2013$761,202 across houses, townhouses and a condo.',
    built: 'Lennar \u2014 announced September 2023, homes built from 2024. Newly opened and still building; gated, in MidTown.',
    amenities: 'The town\u2019s most distinctive: a waterpark amenity with lazy river, water slide, luau beach, pools and a pirate-ship water feature, plus parks, sports courts and the Shipwreck Tavern restaurant.',
    forSale: 'About 39 listings (35 single-family, 3 townhouses, 1 condo).',
    sourceNote: 'Lennar newsroom, Sept 2023; Homes by Marco subdivision index.',
  },
  {
    id: 'webbs-reserve',
    district: 'midtown',
    name: 'Webb\u2019s Reserve',
    summary: 'Condos from $210K \u00b7 ~34 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/07/WebbsReserve_Clubhouse__Website.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/webbs-reserve/',
    priceText: 'Condos $210K+ · homes to $812K',
    priceMinK: 210,
    priceMaxK: 812,
    forSaleCount: 34,
    status: 'Still building',
    gated: true,
    golf: true,
    builder: 'Lennar',
    ageShort: 'Golf opened Nov 2024',
    narrative: 'MidTown\u2019s golf address. Webb\u2019s Reserve wraps homes and condos around an 18-hole Nicklaus Design course (opened November 2024) with a high-tech Aqua Toptracer range, putt-putt, resort pool, and the Overlook Bar & Grille. Condos from $210K make it the other affordable golf door in town. Best for golfers who want new construction and prices that span condo to estate. Best features: the Nicklaus course and the range scene.',
    takeShort: 'MidTown\u2019s golf address — a Nicklaus Design course, condos from $210K, homes to $812K.',
    priceRange: 'Condos $209,700\u2013$495,000; single-family $597,700\u2013$811,519.',
    built: 'Lennar country-club community, announced 2023; the Nicklaus Design golf course opened for play in November 2024. Still building.',
    amenities: 'Private: 18-hole Nicklaus Design course, an Aqua Toptracer driving range, putt-putt course, resort-style pool, the Overlook Bar & Grille, pickleball and tennis, fitness center and spa.',
    forSale: 'About 34 listings (11 single-family + 23 condos).',
    sourceNote: 'Homes by Marco subdivision index; The Golf Wire; official neighborhoods page.',
  },
  {
    id: 'willowgreen',
    district: 'westtown',
    name: 'Willowgreen',
    summary: '$291K\u2013$589K \u00b7 ~16 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/07/Willowgreen-TEMP-Website-Photo.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/willowgreen/',
    priceText: '$291K–$589K',
    priceMinK: 291,
    priceMaxK: 589,
    forSaleCount: 16,
    status: 'Still building',
    gated: true,
    builder: 'Lennar',
    ageShort: 'Built early–mid 2020s',
    narrative: 'Low-maintenance lake living by the Square. These gated Lennar coach homes trade yard work for a private pool, pool house, and fire pit, with Lake Babcock out the back and Founder\u2019s Square a short stroll away. Best for snowbirds and downsizers who want the lake-and-downtown lifestyle in a condo format. Best features: the location — lake on one side, the Square on the other.',
    takeShort: 'Low-maintenance lake living by the Square — gated coach homes, private pool, Founder\u2019s Square a short stroll away.',
    priceRange: 'Active listings $290,997\u2013$589,000 for condos, plus one townhouse at $479,000.',
    built: 'Lennar luxury coach homes on Lake Babcock, steps from Founder\u2019s Square. Built in the early-to-mid 2020s, with new product still releasing.',
    amenities: 'Private pool, pool house and fire pit; gated; lakefront setting.',
    forSale: 'About 16 listings (15 condos + 1 townhouse).',
    sourceNote: 'Homes by Marco subdivision index; official neighborhoods page.',
  },
  {
    id: 'waterview-landing',
    district: 'westtown',
    name: 'Waterview Landing',
    summary: '$489K\u2013$879K \u00b7 ~9\u201315 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2022/04/WaterviewLanding-website.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/waterview-landing/',
    priceText: '$489K–$879K',
    priceMinK: 489,
    priceMaxK: 879,
    forSaleCount: 12,
    status: 'Still building',
    gated: true,
    builder: 'Toll Brothers',
    ageShort: 'Opened mid-2020s',
    narrative: 'WestTown\u2019s luxury tier. Toll Brothers is building just 96 homes on a Lake Babcock peninsula, and every homesite has a water view — there isn\u2019t a bad lot in the place. Best for luxury buyers who want new construction and the lake without joining a golf club. Best features: guaranteed water views, Toll Brothers build quality, and small-enclave privacy.',
    takeShort: 'WestTown\u2019s luxury tier — 96 Toll Brothers homes on a lake peninsula, every homesite with a water view.',
    priceRange: 'New homes from the mid-$400s; active single-family listings $489,000\u2013$879,000.',
    built: 'Toll Brothers \u2014 96 luxury single-family homes on a peninsula in Lake Babcock. Mid-2020s, still building.',
    amenities: 'Every homesite has water views of the lake or conservation land; no separate private clubhouse.',
    forSale: 'About 9\u201315 listings.',
    sourceNote: 'Official neighborhoods page; Homes by Marco subdivision index.',
  },
  {
    id: 'regency',
    district: 'midtown',
    name: 'Regency at Babcock Ranch (55+)',
    summary: '$570K\u2013$882K resales \u00b7 4 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2022/10/Regency_Website-Image.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/regency-at-babcock-ranch/',
    priceText: '$570K–$882K resales',
    priceMinK: 570,
    priceMaxK: 882,
    forSaleCount: 4,
    status: 'Still building',
    gated: true,
    fiftyFivePlus: true,
    builder: 'Toll Brothers',
    ageShort: 'Selling since ~2023',
    narrative: 'The established 55+ flagship. Toll Brothers\u2019 gated Regency was the Ranch\u2019s first active-adult neighborhood, and it shows in the depth of its clubhouse, lifestyle programming, pool, and courts — a social calendar that runs itself. Best for active adults who want the proven 55+ scene rather than a promise. Best features: the full-service club lifestyle and Toll Brothers finishes.',
    takeShort: 'The established 55+ flagship — Toll Brothers\u2019 gated community with the deepest clubhouse scene in town.',
    priceRange: 'Active resales $570,000\u2013$882,000; builder collections start around $399,000.',
    built: 'Toll Brothers \u2014 the town\u2019s first 55+ active-adult neighborhood; gated, selling since about 2023 and still building.',
    amenities: 'Private 55+ amenity: resort-style clubhouse, pool, pickleball, tennis, fitness center and lifestyle programming.',
    forSale: '4 resale homes, plus builder quick-move-ins through Toll Brothers.',
    sourceNote: 'Homes by Marco subdivision index; official builder page.',
  },
  {
    id: 'townwalk',
    district: 'westtown',
    name: 'Townwalk',
    summary: '$248K\u2013$285K \u00b7 4 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2023/05/Townwalk_Jefferson_Model_Website-Image.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/townwalk/',
    priceText: '$248K–$285K',
    priceMinK: 248,
    priceMaxK: 285,
    forSaleCount: 4,
    status: 'Established',
    builder: 'Park Square Homes',
    ageShort: 'Opened mid-2020s',
    narrative: 'The lock-and-leave townhome play. Townwalk\u2019s two-story townhomes start in the high $240s — among the lowest entry prices in the Ranch — and sit steps from Founder\u2019s Square dining, shops, and events. Best for snowbirds, downsizers, and first buyers who\u2019d rather be at the Square than mowing a lawn. Best features: price, and the most walkable address in this part of town.',
    takeShort: 'The lock-and-leave townhome play — from the high $240s, steps from Founder\u2019s Square dining and events.',
    priceRange: 'Active townhouse listings $248,150\u2013$285,000 \u2014 among the lowest entry prices in town.',
    built: 'The town\u2019s first townhome community \u2014 242 two-story residences on 25 acres by Park Square Homes, steps from Founder\u2019s Square. Mid-2020s.',
    amenities: 'No private amenity center; parks on both sides and walkable to Founder\u2019s Square dining and shops.',
    forSale: '4 townhouses.',
    sourceNote: 'Homes by Marco subdivision index; Greater Fort Myers Realty.',
  },
  {
    id: 'sabal-glen',
    district: 'midtown',
    name: 'Sabal Glen',
    summary: 'From the high $200s \u00b7 ~6 for sale',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2025/02/Sabal-Glen-Website-Temp.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/sabal-glen/',
    priceText: 'From the high $200s',
    priceMinK: 280,
    forSaleCount: 6,
    status: 'New · opened 2025',
    builder: 'Lennar',
    ageShort: 'Sales launched Nov 2025',
    narrative: 'The early-in MidTown play. Lennar launched Sabal Glen in November 2025 from the high $200s, steps from where MidTown\u2019s shops, dining, and entertainment are planned — you\u2019re buying ahead of the downtown, at today\u2019s prices. Best for buyers comfortable betting on the district growing up around them. Best features: position, newness, and the lowest new-home entry in MidTown.',
    takeShort: 'The early-in MidTown play — Lennar\u2019s newest, from the high $200s, steps from MidTown\u2019s future downtown.',
    priceRange: 'Homes from the high $200,000s; active listings average about $419,000.',
    built: 'Lennar, in MidTown \u2014 sales launched in November 2025 and the first homes were built in 2026. Newly opened, still building.',
    amenities: 'Non-gated; no private amenity center identified \u2014 steps from MidTown\u2019s future shopping and dining, and uses the town-wide amenities.',
    forSale: 'About 6 listings, plus builder inventory.',
    sourceNote: 'Babcock Ranch Telegraph, Nov 2025; Homes by Marco.',
  },
  {
    id: 'alta-key',
    district: 'midtown',
    name: 'Alta Key (55+)',
    summary: 'From the mid-$200s \u00b7 pre-sales',
    photoUrl: 'https://babcockranch.com/wp-content/uploads/2026/06/Alta-Key-Entry_Rendering.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/alta-key/',
    priceText: 'From the mid-$200s',
    priceMinK: 250,
    status: 'Pre-sales · opened 2026',
    gated: true,
    fiftyFivePlus: true,
    builder: 'High Point Living',
    ageShort: 'Opened 2026',
    narrative: 'The disruptive 55+ value. Alta Key opened in 2026 with single-family homes from the mid-$200s — possible because you buy the home and lease the land on a 99-year lease, trading land ownership for a much lower entry price and predictable costs. Gated, with a resort pool, putting green, pickleball, and lawn care included. Best for 55+ buyers who prioritize cash flow and simplicity. Know the trade-off before you fall in love: the ownership math is different here.',
    takeShort: 'The disruptive 55+ value — homes from the mid-$200s on a 99-year land lease. Different ownership math; know the trade-off.',
    priceRange: 'Single-family homes from the mid-$200,000s \u2014 buyers purchase the home and lease the land on a 99-year land lease, which keeps the entry price down.',
    built: 'The town\u2019s newest neighborhood (High Point Living). Sales center opened in 2026; pre-sales under way, models opening later in 2026.',
    amenities: 'Gated; resort-style pool, putting green, pickleball and fire pits; lawn care and community management included by the operator.',
    forSale: 'Pre-sales only \u2014 no MLS listings yet.',
    sourceNote: 'Official neighborhoods page; Babcock Ranch Telegraph; alta-key.com.',
  },
  {
    id: 'sawgrass-lakes',
    district: 'westtown',
    name: 'Sawgrass Lakes',
    summary: 'Coming soon \u00b7 not yet selling',
    photoUrl: 'https://babcockranch.com/nitropack_static/ZmAunjjXOPhVQlUxjxaOPuKXgJYJXDKv/assets/images/optimized/rev-364421c/babcockranch.com/wp-content/uploads/2026/10/sawgrass-lakes-sanibel-elevation.jpg',
    photoPage: 'https://babcockranch.com/neighborhoods/sawgrass-lakes/',
    priceText: 'Pricing not released',
    status: 'Coming soon',
    gated: true,
    builder: 'Pulte + Toll Brothers',
    ageShort: 'Announced Jun 2026',
    narrative: 'The future luxury bet. Announced in June 2026, this gated 138-home enclave by Pulte and Toll Brothers along Saw Palmetto Parkway has no pricing and nothing to buy yet. Best for patient luxury buyers who want first pick when sales open. Best features: the newest land in WestTown, with lake and preserve views — if you\u2019re willing to wait for it.',
    takeShort: 'The future luxury bet — gated Pulte + Toll Brothers enclave, announced 2026, no pricing yet.',
    priceRange: 'Pricing not yet released.',
    built: 'Announced June 2026 \u2014 a gated luxury neighborhood of 138 single-family homes by Pulte Homes and Toll Brothers in WestTown, along Saw Palmetto Parkway. Coming soon.',
    amenities: 'Gated, with lake and preserve views; the amenity package has not been detailed yet.',
    forSale: 'None \u2014 pre-sales have not started.',
    sourceNote: 'Official neighborhoods page; Babcock Ranch Telegraph.',
  },
  {
    id: 'the-flatwoods',
    district: 'midtown',
    name: 'The Flatwoods (rentals)',
    summary: 'Rental only \u00b7 nothing for sale',
    photoUrl: 'https://irp.cdn-website.com/fcea39f6/dms3rep/multi/opt/jByG7KtwMdlzLBFVRHsV132KBXudaVvuKDnWo9LO-be0cc57ef11f4e1c656a6cf949e6c257-1920w.jpg',
    photoPage: 'https://theflatwoodsbabcockranch.com/',
    priceText: 'Rental only',
    status: 'Rental community',
    builder: 'Rental community',
    ageShort: 'Single-family rentals',
    narrative: 'The try-before-you-buy neighborhood. The Flatwoods rents single-family homes — three to five bedrooms, fenced yards, its own dog park, and full access to the Ranch\u2019s pools and amenities — so you can live the lifestyle before committing to it. Best for newcomers testing Babcock Ranch, snowbirds, and anyone between houses. Best features: real-house rental stock with zero ownership strings.',
    takeShort: 'The try-before-you-buy — single-family rentals with fenced yards and full amenity access.',
    priceRange: 'Rental only \u2014 current lease rates are not listed here; check the official rentals page.',
    built: 'A single-family rental neighborhood of 3- to 5-bedroom homes with fenced backyards.',
    amenities: 'Its own dog park, plus full access to the town-wide pools and amenities.',
    forSale: 'None \u2014 this is a rental community.',
    sourceNote: 'Official Babcock Ranch rentals page.',
  },
];

